import React, { ChangeEventHandler } from 'react';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';

import {
  SendCommunicationCustomersSelection as CustomersSelection,
  SendCommunicationCustomersSelection,
  TargetType,
} from 'types';
import Card from 'components/molecules/card';

import { Container, Props, SelectTargetType } from './utils';
import TargetClientType from './target-client-type';
import TargetDVNumbers from './target-dv-numbers';

const TargetCustomerAndStations = ({
  formik,
  stationsConcerned,
  isBulkCommunication,
  targetType,
  ServicesPassengersConcernedByCommunicationErrorMessage,
  onTouch,
  setTargetType,
  setOdsSummaryContent,
}: Props): React.ReactElement => {
  const stations = [...stationsConcerned];
  const excludedStations = [...stationsConcerned];
  if (formik.values?.customersSelection === CustomersSelection.Ascending && !isBulkCommunication) {
    stations.pop();
  }
  if (formik.values?.customersSelection === CustomersSelection.Descending && !isBulkCommunication) {
    stations.shift();
    excludedStations.pop();
  }

  const handleCustomersSelectedChange = (e: CheckboxChangeEvent) => {
    onTouch();
    formik.setFieldValue('impactedStations', []);
    formik.setFieldValue('excludedStations', []);
    formik.setFieldValue('customersSelection', e.target.value);
  };

  const handleChangeCheckAllStations = (e: CheckboxChangeEvent) => {
    onTouch();
    const allUicStations = stations.map((station) => station.uicStation);
    formik.setFieldValue('impactedStations', e.target.checked ? allUicStations : []);
  };

  const handleChangeCheckStations = (list: CheckboxValueType[]): void => {
    onTouch();
    formik.setFieldValue('impactedStations', list as string[]);
  };

  const handleChangeCheckExcludeStations = (list: CheckboxValueType[]) => {
    onTouch();
    formik.setFieldValue('excludedStations', list as string[]);
  };

  const handleChangeDvNumbers: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    onTouch();
    formik.setFieldValue('DVNumbers', e.target.value);
  };

  const handleSelectTargetType = (id: TargetType) => {
    onTouch();
    setTargetType(id);
    formik.resetForm();
    /**
     * The TargetType is exclusively utilized on the frontend in the first step to handle the SelectTargetType
     * SO ensure that the customersSelection variable has the correct value
     */
    if (id === TargetType.DV_NUMBERS) {
      formik.setFieldValue('customersSelection', SendCommunicationCustomersSelection.DVNumbers);
      // reset the odsSummaryContent if target is DV_NUMBERS
      setOdsSummaryContent([]);
    } else {
      formik.setFieldValue('customersSelection', SendCommunicationCustomersSelection.All);
    }
  };

  const shouldDisplaySelectedStationsCol = formik.values?.customersSelection !== CustomersSelection.All;
  const shouldDisplayExcludedStationsCol =
    !isBulkCommunication && formik.values?.customersSelection === CustomersSelection.Descending;
  const isAllStationsChecked = formik.values.impactedStations.length === stations.length;
  const hasNoStationsInCommon = stationsConcerned && stationsConcerned?.length === 0;
  const hasOdsSummaryButton =
    formik.values?.customersSelection === CustomersSelection.Descending &&
    formik.values.impactedStations.length > 0 &&
    formik.values.excludedStations.length > 0;

  return (
    <Container>
      <Card full>
        {!isBulkCommunication && (
          <SelectTargetType targetType={targetType} handleSelectTargetType={handleSelectTargetType} />
        )}

        {targetType === TargetType.DV_NUMBERS && !isBulkCommunication && (
          <TargetDVNumbers
            formik={formik}
            handleChangeDvNumbers={handleChangeDvNumbers}
            httpErrorMessage={ServicesPassengersConcernedByCommunicationErrorMessage}
          />
        )}

        {targetType === TargetType.CLIENT_TYPE && (
          <TargetClientType
            formik={formik}
            isAllStationsChecked={isAllStationsChecked}
            hasNoStationsInCommon={hasNoStationsInCommon}
            shouldDisplaySelectedStationsCol={shouldDisplaySelectedStationsCol}
            shouldDisplayExcludedStationsCol={shouldDisplayExcludedStationsCol}
            hasOdsSummaryButton={hasOdsSummaryButton}
            isBulkCommunication={isBulkCommunication}
            stations={stations}
            stationsConcerned={stationsConcerned}
            excludedStations={excludedStations}
            handleCustomersSelectedChange={handleCustomersSelectedChange}
            handleChangeCheckStations={handleChangeCheckStations}
            handleChangeCheckAllStations={handleChangeCheckAllStations}
            handleChangeCheckExcludeStations={handleChangeCheckExcludeStations}
            setOdsSummaryContent={setOdsSummaryContent}
          />
        )}
      </Card>
    </Container>
  );
};

export default TargetCustomerAndStations;
